import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { FormProgress } from "../components/FormProgress";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, MapPin, AlertTriangle, Pencil, Trash2, Eye, Phone, Mail, Calendar } from "lucide-react";
import { FileUpload } from "../components/FileUpload";
import { EntityNotes } from "../components/EntityNotes";
import { MapView } from "../components/MapView";
import { PhoneInput } from "../components/PhoneInput";
import { ContactActions } from "../components/ContactActions";
import { SearchWithRecent } from "../components/SearchWithRecent";
import { useConfirm } from "../components/ConfirmDialog";
import { brl, formatUSPhone } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { TOP_LANGUAGES, languageLabel } from "../lib/languages";
import { titleCase, tCase } from "../lib/textCase";
import { FormField } from "../components/ui/form-field";
import { HouseDetailsForm } from "../components/clients/HouseDetailsForm";
import { HouseDetailsView } from "../components/clients/HouseDetailsView";
import { ClientViewDialog } from "../components/clients/ClientViewDialog";
import { ScheduleOnboardingDialog, ScheduleOnboardingForm } from "../components/clients/ScheduleOnboardingDialog";
import { DatePickerUS } from "../components/ui/date-picker-us";
import { refreshSidebarCounts } from "../lib/sidebarBus";
import { toast } from "sonner";

const emptyClient = {
  name: "",
  business_name: "",
  phone: "",
  email: "",
  address: "",
  apt_number: "",
  door_code: "",
  city: "",
  state: "",
  zip_code: "",
  lat: null,
  lng: null,
  property_type: "Residencial",
  frequency: "Semanal",
  value: 0,
  observations: "",
  photos: [],
  has_whatsapp: false,
  has_telegram: false,
  has_instagram: false,
  has_facebook: false,
  instagram_handle: "",
  facebook_handle: "",
  language: "",
  cleaning_checklist: [],
  auto_recurrence: true,
  contract_start_date: "",
  contract_end_date: "",
  // ── Cadastro detalhado da casa ──
  has_pets: null,
  pet_names: "",
  property_subtype: "",
  bedrooms: "",
  bathrooms: "",
  square_feet: "",
  rooms_to_clean: [],
  cleaning_types: [],
  frequency_options: [],
  preferred_days: [],
  preferred_times: [],
  supplies_provided: null,
  additional_services: [],
  rate_weekly: "",
  rate_biweekly: "",
  rate_monthly: "",
  rate_other: "",
  access_instructions: "",
  payment_due_day: "",
  schedule_config: null,
};

// US states for dropdown
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

function formatFullAddress(c) {
  if (!c) return "";
  const line1 = [c.address, c.apt_number ? `Apt ${c.apt_number}` : ""]
    .filter(Boolean).join(" ");
  const line2 = [c.city, c.state, c.zip_code].filter(Boolean).join(", ");
  return [line1, line2].filter(Boolean).join(" — ");
}


export default function Clients() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState(() => {
    try {
      return localStorage.getItem("cm:clients:sort") || "name_asc";
    } catch {
      return "name_asc";
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [routineFilter, setRoutineFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [scheduleFor, setScheduleFor] = useState(null);
  const { confirm, ConfirmModal } = useConfirm();
  const location = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    try { localStorage.setItem("cm:clients:sort", sortBy); } catch { /* ignore */ }
  }, [sortBy]);

  async function load() {
    const { data } = await api.get("/clients");
    setClients(data);
  }
  useEffect(() => {
    load();
  }, []);

  // Detecta dados pré-preenchidos vindos do menu Estimate → abre o dialog
  useEffect(() => {
    const prefilled = location.state?.prefilled;
    if (prefilled) {
      setEditing({ ...prefilled });
      setOpen(true);
      // Limpa o state da rota para não reabrir em navegações futuras
      nav(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, nav]);

  // Detecta ?schedule={id}, ?focus={id} ou ?edit={id} vindos de outras páginas
  useEffect(() => {
    if (clients.length === 0) return;
    const params = new URLSearchParams(location.search);
    const scheduleId = params.get("schedule");
    const focusId = params.get("focus");
    const editId = params.get("edit");
    const id = scheduleId || focusId || editId;
    if (!id) return;
    const target = clients.find((c) => c.id === id);
    if (target) {
      if (scheduleId) {
        setScheduleFor(target);
      } else {
        setEditing(target);
        setOpen(true);
      }
      nav(location.pathname, { replace: true });
    }
  }, [clients, location.search, location.pathname, nav]);

  // Detecta ?routine=without|with vindo do Dashboard → aplica o filtro de rotina
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const r = params.get("routine");
    if (r === "without" || r === "with") {
      setRoutineFilter(r);
      nav(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, nav]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const matched = !query
      ? clients
      : clients.filter((c) => {
          const fields = [
            c.name,
            c.address,
            c.city,
            c.state,
            c.zip_code,
            c.email,
            c.phone,
            formatUSPhone(c.phone),
          ];
          return fields.some((f) => f && String(f).toLowerCase().includes(query));
        });

    const hasRoutine = (c) => !!(c.schedule_config && c.schedule_config.team_id);
    const byRoutine =
      routineFilter === "with"
        ? matched.filter(hasRoutine)
        : routineFilter === "without"
          ? matched.filter((c) => !hasRoutine(c))
          : matched;

    const arr = [...byRoutine];
    const collator = new Intl.Collator("en-US", { sensitivity: "base" });
    const num = (v) => Number(v) || 0;
    const ts = (c) => new Date(c.created_at || 0).getTime();
    switch (sortBy) {
      case "name_desc":
        arr.sort((a, b) => collator.compare(b.name || "", a.name || ""));
        break;
      case "newest":
        arr.sort((a, b) => ts(b) - ts(a));
        break;
      case "oldest":
        arr.sort((a, b) => ts(a) - ts(b));
        break;
      case "value_desc":
        arr.sort((a, b) => num(b.value) - num(a.value));
        break;
      case "value_asc":
        arr.sort((a, b) => num(a.value) - num(b.value));
        break;
      case "name_asc":
      default:
        arr.sort((a, b) => collator.compare(a.name || "", b.name || ""));
    }
    return arr;
  }, [clients, q, sortBy, routineFilter]);

  const routineStats = useMemo(() => {
    const total = clients.length;
    const withRoutine = clients.filter(
      (c) => c.schedule_config && c.schedule_config.team_id,
    ).length;
    return { total, withRoutine, without: total - withRoutine };
  }, [clients]);

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Carteira")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("Clientes")}</h1>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <SearchWithRecent
            value={q}
            onChange={setQ}
            placeholder={t("Localizar por nome, e-mail, telefone, endereço…")}
            testId="clients-search"
            storageKey="cm:recent:clients"
            className="w-full sm:w-auto sm:min-w-[240px]"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="clients-sort"
            aria-label={t("Ordenar clientes")}
            title={t("Ordenar clientes")}
            className="cm-input flex-1 min-w-[140px] sm:flex-none sm:!w-[180px]"
          >
            <option value="name_asc">{t("A → Z (nome)")}</option>
            <option value="name_desc">{t("Z → A (nome)")}</option>
            <option value="newest">{t("Mais recentes (criação)")}</option>
            <option value="oldest">{t("Mais antigos (criação)")}</option>
            <option value="value_desc">{t("Maior valor")}</option>
            <option value="value_asc">{t("Menor valor")}</option>
          </select>
          <select
            value={routineFilter}
            onChange={(e) => setRoutineFilter(e.target.value)}
            data-testid="clients-routine-filter"
            aria-label={t("Filtrar por rotina")}
            title={t("Filtrar por rotina")}
            className="cm-input flex-1 min-w-[130px] sm:flex-none sm:!w-[160px]"
          >
            <option value="all">{t("Todas as rotinas")}</option>
            <option value="with">{t("Com rotina ativa")}</option>
            <option value="without">{t("Sem rotina")}</option>
          </select>
          <Button
            asChild
            variant="outline"
            data-testid="clients-map-btn"
            className="flex-1 min-w-[120px] sm:flex-initial"
          >
            <a href="/clients/map" className="inline-flex items-center gap-1 justify-center">
              <MapPin size={16} /> {t("Ver mapa")}
            </a>
          </Button>
          <Button
            data-testid="clients-new-btn"
            onClick={() => {
              setEditing({ ...emptyClient });
              setOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-initial"
          >
            <Plus size={16} className="mr-1" /> {t("Novo cliente")}
          </Button>
        </div>
      </div>

      {routineStats.without > 0 && (
        <button
          type="button"
          data-testid="clients-routine-goal-banner"
          onClick={() => setRoutineFilter(routineFilter === "without" ? "all" : "without")}
          className="w-full text-left mb-4 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors px-4 py-3 flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Calendar size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-amber-900">
              {t("{without} de {total} clientes ainda sem rotina", {
                without: routineStats.without,
                total: routineStats.total,
              })}
            </div>
            <div className="text-xs text-amber-700">
              {routineFilter === "without"
                ? t("Mostrando só os sem rotina — clique para ver todos.")
                : t("Configure uma agenda recorrente e transforme em receita previsível. Clique para filtrar.")}
            </div>
          </div>
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <ClientCard
            key={c.id}
            client={c}
            onView={() => setViewing(c)}
            onEdit={() => {
              setEditing(c);
              setOpen(true);
            }}
            onSchedule={() => setScheduleFor(c)}
            onDeleted={load}
            confirm={confirm}
          />
        ))}
        {filtered.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            {clients.length === 0 ? (
              <>
                <div className="text-lg font-semibold text-slate-700 mb-2">
                  {t("Nenhum cliente cadastrado ainda")}
                </div>
                <div className="text-sm">
                  {t("Clique em")} <strong>"{t("Novo cliente")}"</strong> {t("para começar.")}
                </div>
              </>
            ) : (
              <>
                <div className="text-lg font-semibold text-slate-700 mb-2">
                  Nenhum cliente encontrado
                </div>
                <div className="text-sm">
                  Tente outros termos de busca ou{" "}
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    limpar filtro
                  </button>
                  .
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <ClientDialog
        open={open}
        onOpenChange={setOpen}
        client={editing}
        onSaved={(createdClient) => {
          setOpen(false);
          load();
          if (createdClient) {
            // Brand new client → offer to schedule recurring cleanings now.
            setScheduleFor(createdClient);
          }
        }}
      />
      <ScheduleOnboardingDialog
        client={scheduleFor}
        open={!!scheduleFor}
        onClose={() => {
          setScheduleFor(null);
          load();
        }}
      />
      <ClientViewDialog client={viewing} onClose={() => setViewing(null)} />
      {ConfirmModal}
    </AdminLayout>
  );
}

function ClientCard({ client, onView, onEdit, onSchedule, onDeleted, confirm }) {
  const { t } = useI18n();
  async function remove() {
    // Descobre quantos agendamentos futuros virariam órfãos na agenda.
    let futureCount = 0;
    try {
      const { data } = await api.get(`/clients/${client.id}/future-jobs`);
      futureCount = data?.count || 0;
    } catch {
      /* segue com o aviso padrão */
    }
    const hasFuture = futureCount > 0;
    const ok = await confirm({
      title: t('Excluir cliente "{name}"?', { name: client.name }),
      description: hasFuture
        ? t(
            "Este cliente tem {n} agendamento(s) futuro(s). Eles serão removidos junto para não virarem agendamentos órfãos (fantasmas) na agenda. As limpezas já finalizadas ficam no histórico.",
            { n: futureCount },
          )
        : t(
            "Todas as limpezas relacionadas ainda serão preservadas no histórico, mas o cadastro do cliente será removido.",
          ),
      actionLabel: hasFuture
        ? t("Excluir cliente e {n} agendamento(s)", { n: futureCount })
        : t("Excluir cliente"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(
        `/clients/${client.id}${hasFuture ? "?remove_jobs=true" : ""}`,
      );
      toast.success(
        hasFuture
          ? t("Cliente e {n} agendamento(s) excluídos", { n: futureCount })
          : t("Cliente excluído"),
      );
      refreshSidebarCounts();
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  // Detecta endereço incompleto (sem rua ou sem CEP) — exibe aviso amarelo
  const missingStreet = !client.address || client.address.trim().length < 4;
  const missingZip = !client.zip_code;
  const addressIncomplete = missingStreet || missingZip;
  return (
    <div className="cm-card p-5" data-testid={`client-card-${client.id}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
            <span className="truncate">{client.name}</span>
            {client.business_name && (
              <span
                data-testid={`client-business-${client.id}`}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-normal truncate"
                title={client.business_name}
              >
                {client.business_name}
              </span>
            )}
            {client.schedule_config && client.schedule_config.team_id && (
              <span
                data-testid={`client-routine-badge-${client.id}`}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium flex-shrink-0"
                title={t("Este cliente tem uma rotina de limpeza configurada")}
              >
                <Calendar size={10} /> {t("Rotina ativa")}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 flex items-start gap-1 mt-0.5">
            <MapPin size={12} className="mt-0.5 flex-shrink-0" />
            <span className="break-words">
              {formatFullAddress(client) || client.address || "—"}
            </span>
          </div>
          {addressIncomplete && (
            <div
              data-testid={`client-address-warning-${client.id}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800"
              title={`${t("Endereço incompleto:")} ${missingStreet ? t("sem rua") : ""}${missingStreet && missingZip ? ` ${t("e")} ` : ""}${missingZip ? t("sem CEP/ZIP") : ""}`}
            >
              {t("⚠ Endereço incompleto")}
            </div>
          )}
          {client.door_code && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 font-mono">
              🔑 {client.door_code}
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={onView}
            data-testid={`client-view-${client.id}`}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title={t("Visualizar cliente")}
            aria-label={t("Visualizar cliente")}
          >
            <Eye size={18} />
          </button>
          <button
            onClick={onSchedule}
            data-testid={`client-schedule-${client.id}`}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
            title={t("Agendar limpeza")}
            aria-label={t("Agendar limpeza")}
          >
            <Calendar size={18} />
          </button>
          <button
            onClick={onEdit}
            data-testid={`client-edit-${client.id}`}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            title={t("Editar")}
            aria-label={t("Editar")}
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={remove}
            data-testid={`client-delete-${client.id}`}
            className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
            title={t("Excluir")}
            aria-label={t("Excluir")}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
        <div>
          <div className="text-slate-500 text-xs">{t("Tipo")}</div>
          <div>{client.property_type ? t(client.property_type) : "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">{t("Frequência")}</div>
          <div>{client.frequency ? t(client.frequency) : "—"}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">{t("Valor")}</div>
          <div className="font-semibold">{brl(client.value)}</div>
        </div>
        <div>
          <div className="text-slate-500 text-xs">{t("Telefone")}</div>
          <div className="font-mono text-xs">
            {client.phone ? `+1 ${formatUSPhone(client.phone)}` : "—"}
          </div>
        </div>
      </div>
      {(client.has_whatsapp ||
        client.has_telegram ||
        client.has_instagram ||
        client.has_facebook) && (
        <div className="flex flex-wrap gap-1 mt-3">
          {client.has_whatsapp && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              WhatsApp
            </span>
          )}
          {client.has_telegram && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
              Telegram
            </span>
          )}
          {client.has_instagram && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-200">
              Instagram
            </span>
          )}
          {client.has_facebook && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              Facebook
            </span>
          )}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <ContactActions client={client} testIdPrefix={`client-${client.id}-contact`} />
      </div>
      {client.observations && (
        <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span className="whitespace-pre-line">{client.observations}</span>
        </div>
      )}
    </div>
  );
}

function ClientDialog({ open, onOpenChange, client, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(client || emptyClient);
  const { confirm } = useConfirm();
  useEffect(() => {
    setForm(client || emptyClient);
  }, [client, open]);

  function set(k, v) {
    let val = v;
    if (k === "name") val = titleCase(v);
    else if (k === "email") val = (v || "").toLowerCase();
    setForm((f) => ({ ...f, [k]: val }));
  }

  // Define a localização manualmente (arrastar pino / clique / colar coords).
  function setManualLocation(lat, lng) {
    setForm((f) => ({
      ...f,
      lat: Number(lat.toFixed(6)),
      lng: Number(lng.toFixed(6)),
      location_manual: true,
    }));
  }

  const [coordsInput, setCoordsInput] = useState("");
  function applyCoordsInput() {
    const s = (coordsInput || "").trim();
    if (!s) return;
    // Aceita "lat, lng" ou links do Google Maps (@lat,lng ou q=lat,lng)
    let m =
      s.match(/@(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/) ||
      s.match(/[?&]q=(-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)/) ||
      s.match(/(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
    if (!m) {
      toast.error(t("Não reconheci as coordenadas. Cole 'lat, long' ou um link do Google Maps."));
      return;
    }
    const lat = parseFloat(m[1]);
    const lng = parseFloat(m[2]);
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      toast.error(t("Coordenadas fora do intervalo válido."));
      return;
    }
    setManualLocation(lat, lng);
    setCoordsInput("");
    toast.success(t("Localização aplicada"));
  }

  async function save({ andSchedule = false, silent = false, extra = null } = {}) {
    try {
      const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const intOrNull = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
      };
      const body = {
        ...form,
        ...(extra || {}),
        value: Number(form.value) || 0,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        bedrooms: intOrNull(form.bedrooms),
        bathrooms: intOrNull(form.bathrooms),
        square_feet: intOrNull(form.square_feet),
        payment_due_day: intOrNull(form.payment_due_day),
        rate_weekly: numOrNull(form.rate_weekly),
        rate_biweekly: numOrNull(form.rate_biweekly),
        rate_monthly: numOrNull(form.rate_monthly),
        rate_other: numOrNull(form.rate_other),
      };
      if (client?.id) {
        await api.patch(`/clients/${client.id}`, body);
        // Se o horário do agendamento mudou, reagenda o horário das casas
        // FUTURAS já agendadas deste cliente (sem mexer em datas/iniciadas).
        const oldTime = client?.schedule_config?.time;
        const newTime = body.schedule_config?.time || form.schedule_config?.time;
        if (newTime && newTime !== oldTime) {
          try {
            const { data: r } = await api.post("/jobs/reschedule-future-time", {
              client_id: client.id,
              time: newTime,
              team_id: form.schedule_config?.team_id || undefined,
            });
            if (!silent && r?.updated > 0) {
              toast.success(
                t("Horário atualizado em {n} casa(s) futura(s)", { n: r.updated }),
              );
            }
          } catch {
            /* não bloqueia o salvamento */
          }
        }
        if (silent) return;
        toast.success(t("Cliente salvo"));
        // Em edição: se "Agendar agora" foi clicado, abre o dialog de schedule
        // com o cliente atualizado. Senão, fluxo normal (fecha).
        onSaved(andSchedule ? { ...client, ...body } : null);
      } else {
        const { data: created } = await api.post("/clients", body);
        if (silent) return;
        toast.success(t("Cliente salvo"));
        refreshSidebarCounts();
        // Cliente novo SEMPRE abre o schedule onboarding dialog
        onSaved(created);
      }
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail));
      else throw e;
    }
  }

  const f = form;
  const [tab, setTab] = useState("general");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="client-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {client?.id ? t("Editar Cliente") : t("Novo Cliente")}
          </DialogTitle>
        </DialogHeader>

        <ClientFormProgress form={f} />

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general" data-testid="client-tab-general">
              {t("Dados gerais")}
            </TabsTrigger>
            <TabsTrigger value="house" data-testid="client-tab-house">
              {t("Cadastrar casa")}
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="client-tab-schedule">
              {t("Agendamento")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t("Nome Completo")} req>
            <input
              data-testid="client-field-name"
              className="cm-input"
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Field>
          <Field label="Nome Fantasia / Business Name" hint="Para clientes comerciais (opcional)">
            <input
              data-testid="client-field-business-name"
              className="cm-input"
              placeholder="Ex: Joe's Café LLC"
              value={f.business_name || ""}
              onChange={(e) => set("business_name", e.target.value)}
            />
          </Field>
          <Field label={t("Telefone (US)")}>
            <PhoneInput
              testId="client-field-phone"
              value={f.phone || ""}
              onChange={(v) => set("phone", v)}
            />
          </Field>
          <Field label={t("E-mail")}>
            <input
              data-testid="client-field-email"
              className="cm-input"
              value={f.email || ""}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <div className="md:col-span-2">
            <div className="text-sm font-medium text-slate-700 mb-2">
              {t("Canais de Contato Disponíveis")}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                {
                  k: "has_whatsapp",
                  label: "WhatsApp",
                  on: "bg-emerald-50 border-emerald-300 text-emerald-800",
                  dot: "bg-emerald-500",
                },
                {
                  k: "has_telegram",
                  label: "Telegram",
                  on: "bg-sky-50 border-sky-300 text-sky-800",
                  dot: "bg-sky-500",
                },
                {
                  k: "has_instagram",
                  label: "Instagram",
                  on: "bg-pink-50 border-pink-300 text-pink-800",
                  dot: "bg-pink-500",
                },
                {
                  k: "has_facebook",
                  label: "Facebook",
                  on: "bg-blue-50 border-blue-300 text-blue-800",
                  dot: "bg-blue-500",
                },
              ].map((opt) => {
                const on = !!f[opt.k];
                return (
                  <button
                    type="button"
                    key={opt.k}
                    data-testid={`client-${opt.k}`}
                    onClick={() => set(opt.k, !on)}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      on
                        ? opt.on
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        on ? opt.dot : "bg-slate-300"
                      }`}
                    />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {(f.has_instagram || f.has_facebook) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {f.has_instagram && (
                  <Field label={t("@ do Instagram")}>
                    <input
                      data-testid="client-field-instagram"
                      className="cm-input"
                      placeholder="@usuario"
                      value={f.instagram_handle || ""}
                      onChange={(e) => set("instagram_handle", e.target.value)}
                    />
                  </Field>
                )}
                {f.has_facebook && (
                  <Field label={t("Perfil do Facebook")}>
                    <input
                      data-testid="client-field-facebook"
                      className="cm-input"
                      placeholder="facebook.com/usuario"
                      value={f.facebook_handle || ""}
                      onChange={(e) => set("facebook_handle", e.target.value)}
                    />
                  </Field>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            {f.lat && f.lng ? (
              <div className="mb-3">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <MapPin size={14} />
                    <span data-testid="client-gps-status">
                      {t("✓ Localização:")} {Number(f.lat).toFixed(5)}, {Number(f.lng).toFixed(5)}
                      {f.location_manual && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                          {t("manual")}
                        </span>
                      )}
                    </span>
                  </div>
                  {client?.id && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!(await confirm({
                          title: t("Recalcular GPS deste cliente?"),
                          description: t("Será usado o endereço atual (rua, cidade, estado, ZIP) para obter as coordenadas pelo OpenStreetMap. Isso substitui um ajuste manual."),
                          confirmText: t("Recalcular"),
                        }))) return;
                        try {
                          const { data } = await api.post(`/clients/${client.id}/regeocode`);
                          setForm((p) => ({ ...p, lat: data.lat, lng: data.lng, location_manual: false }));
                          toast.success(t("GPS recalculado"));
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail));
                        }
                      }}
                      data-testid="client-regeocode-btn"
                      className="text-xs text-blue-700 hover:text-blue-900 hover:underline font-medium"
                    >
                      {t("↻ Recalcular pelo endereço")}
                    </button>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mb-1.5 flex items-center gap-1">
                  <MapPin size={12} className="text-blue-500" />
                  {t("Arraste o pino (ou toque no mapa) para ajustar o local exato.")}
                </div>
                <MapView
                  lat={Number(f.lat)}
                  lng={Number(f.lng)}
                  popup={f.address}
                  height={240}
                  editable
                  onPositionChange={setManualLocation}
                  testId="client-map-edit"
                />
                <div className="mt-2 flex items-center gap-2">
                  <input
                    data-testid="client-coords-input"
                    className="cm-input flex-1 text-sm"
                    placeholder={t("Colar link do Google Maps ou 'lat, long'")}
                    value={coordsInput}
                    onChange={(e) => setCoordsInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoordsInput())}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={applyCoordsInput}
                    data-testid="client-coords-apply"
                  >
                    {t("Aplicar")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-3 p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center text-xs text-slate-500">
                <MapPin size={18} className="mx-auto mb-1 text-slate-400" />
                {t("Preencha o endereço abaixo — o mapa aparece após salvar. Ou cole as coordenadas exatas:")}
                <div className="mt-2 flex items-center gap-2 max-w-md mx-auto">
                  <input
                    data-testid="client-coords-input-empty"
                    className="cm-input flex-1 text-sm"
                    placeholder={t("Colar link do Google Maps ou 'lat, long'")}
                    value={coordsInput}
                    onChange={(e) => setCoordsInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoordsInput())}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={applyCoordsInput}
                    data-testid="client-coords-apply-empty"
                  >
                    {t("Aplicar")}
                  </Button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-12">
                <Field label={t("Endereço (rua e número)")} required>
                  <input
                    data-testid="client-field-address"
                    className="cm-input"
                    value={f.address || ""}
                    onChange={(e) => set("address", e.target.value)}
                    placeholder="123 Main St"
                  />
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label={t("Nº do Apartamento")}>
                  <input
                    data-testid="client-field-apt-number"
                    className="cm-input"
                    value={f.apt_number || ""}
                    onChange={(e) => set("apt_number", e.target.value)}
                    placeholder="Apt 4B"
                  />
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label="ZIP">
                  <input
                    data-testid="client-field-zip"
                    className="cm-input"
                    value={f.zip_code || ""}
                    onChange={(e) => set("zip_code", e.target.value)}
                    placeholder="10001"
                  />
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label={t("Código da Porta")}>
                  <input
                    data-testid="client-field-door-code"
                    className="cm-input font-mono"
                    value={f.door_code || ""}
                    onChange={(e) => set("door_code", e.target.value)}
                    placeholder="#1234"
                  />
                </Field>
              </div>
              <div className="md:col-span-8">
                <Field label={t("Cidade")} req>
                  <input
                    data-testid="client-field-city"
                    className="cm-input"
                    value={f.city || ""}
                    onChange={(e) => set("city", e.target.value)}
                    placeholder="New York"
                  />
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label={t("Estado")}>
                  <select
                    data-testid="client-field-state"
                    className="cm-input"
                    value={f.state || ""}
                    onChange={(e) => set("state", e.target.value)}
                  >
                    <option value="">—</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          </div>

          <Field label={t("Tipo de Imóvel")}>
            <select
              className="cm-input"
              value={f.property_type || ""}
              onChange={(e) => set("property_type", e.target.value)}
            >
              <option value="Residencial">{t("Residencial")}</option>
              <option value="Comercial">{t("Comercial")}</option>
              <option value="Condomínio">{t("Condomínio")}</option>
              <option value="Empresarial">{t("Empresarial")}</option>
            </select>
          </Field>
          <Field label={t("Frequência")}>
            <select
              data-testid="client-field-frequency"
              className="cm-input"
              value={f.frequency || ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((p) => ({
                  ...p,
                  frequency: v,
                  ...(["Semanal", "Quinzenal", "Mensal"].includes(v)
                    ? {}
                    : { contract_start_date: "", contract_end_date: "" }),
                }));
              }}
            >
              <option value="Avulsa">{t("Avulsa")}</option>
              <option value="Semanal">{t("Semanal")}</option>
              <option value="Quinzenal">{t("Quinzenal")}</option>
              <option value="Mensal">{t("Mensal")}</option>
            </select>
          </Field>
          <Field label={t("Valor da Limpeza (US$)")}>
            <div className="relative">
              <span
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none font-semibold"
                aria-hidden="true"
              >
                $
              </span>
              <input
                data-testid="client-field-value"
                type="number"
                step="0.01"
                min="0"
                className="cm-input"
                style={{ paddingLeft: "28px" }}
                placeholder="0.00"
                value={f.value}
                onChange={(e) => set("value", e.target.value)}
              />
            </div>
          </Field>

          {["Semanal", "Quinzenal", "Mensal"].includes(f.frequency) && (
            <div className="md:col-span-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors">
                <input
                  type="checkbox"
                  data-testid="client-field-auto-recurrence"
                  checked={!!f.auto_recurrence}
                  onChange={(e) => set("auto_recurrence", e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-semibold text-sm text-blue-900 flex items-center gap-2">
                    {t("↻ Recorrência Automática")}
                  </div>
                  <div className="text-xs text-blue-700 mt-0.5">
                    {t("Ao finalizar uma limpeza deste cliente, agendar automaticamente a próxima em")}{" "}
                    <strong>
                      {f.frequency === "Semanal"
                        ? t("7 dias")
                        : f.frequency === "Quinzenal"
                          ? t("14 dias")
                          : t("30 dias")}
                    </strong>{" "}
                    {t("com a mesma equipe.")}
                  </div>
                </div>
              </label>
            </div>
          )}

          {["Semanal", "Quinzenal", "Mensal"].includes(f.frequency) && (
            <>
              <Field label={t("Data de início do contrato (MM/DD/YYYY)")}>
                <DatePickerUS
                  value={f.contract_start_date || ""}
                  onChange={(v) => set("contract_start_date", v)}
                  testId="client-field-contract-start"
                />
              </Field>
              <Field label={t("Data final do contrato (MM/DD/YYYY) (opcional)")}>
                <DatePickerUS
                  value={f.contract_end_date || ""}
                  onChange={(v) => set("contract_end_date", v)}
                  min={f.contract_start_date || undefined}
                  testId="client-field-contract-end"
                />
              </Field>
              {f.contract_end_date && (
                <div className="md:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  {t("ⓘ Após {date}, a recorrência automática não criará mais novos atendimentos. Os já agendados continuam.", { date: f.contract_end_date })}
                </div>
              )}
            </>
          )}

          <div className="md:col-span-2">
            <Field label={t("Observações da Casa (destaque para equipe)")}>
              <textarea
                data-testid="client-field-observations"
                rows={3}
                className="cm-input"
                value={f.observations || ""}
                onChange={(e) => set("observations", e.target.value)}
                placeholder={t("Ex: cuidado com pets, usar produto X, não limpar quarto trancado…")}
              />
            </Field>
          </div>

          <Field label={t("Idioma Falado pelo Cliente")}>
            <select
              data-testid="client-field-language"
              className="cm-input"
              value={f.language || ""}
              onChange={(e) => set("language", e.target.value)}
            >
              <option value="">{t("— não informado —")}</option>
              {TOP_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {`${l.flag} ${l.label} (${l.native})`}
                </option>
              ))}
            </select>
          </Field>

          <div className="md:col-span-2">
            <Field label={t("Checklist de Limpeza (itens padrão deste cliente)")}>
              <ChecklistEditor
                value={f.cleaning_checklist || []}
                onChange={(v) => set("cleaning_checklist", v)}
              />
            </Field>
          </div>

          <div className="md:col-span-2">
            <Field label={t("Fotos do Local")}>
              <FileUpload
                folder="clients"
                value={f.photos || []}
                onChange={(v) => set("photos", v)}
                testId="client-photos"
              />
            </Field>
          </div>
        </div>
        {client?.id && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <EntityNotes entityType="client" entityId={client.id} />
          </div>
        )}
          </TabsContent>

          <TabsContent value="house" className="mt-4">
            <HouseDetailsForm form={f} setForm={setForm} />
          </TabsContent>
          <TabsContent value="schedule" className="mt-4">
            <ScheduleOnboardingForm
              client={client?.id ? { id: client.id, name: f.name, frequency: f.frequency, preferred_days: f.preferred_days, contract_start_date: f.contract_start_date, contract_end_date: f.contract_end_date, schedule_config: f.schedule_config } : { name: f.name, frequency: f.frequency, preferred_days: f.preferred_days, contract_start_date: f.contract_start_date, contract_end_date: f.contract_end_date, schedule_config: f.schedule_config }}
              autoLoadTeams
              embedded
              submitLabel={t("Adicionar à agenda")}
              updateLabel={t("Atualizar agenda")}
              onConfigChange={(cfg) =>
                setForm((p) => ({ ...p, schedule_config: cfg }))
              }
              onBeforeSchedule={async (cfg) => {
                // Persiste edição de "Dados gerais"/"Cadastrar casa" + a
                // configuração de agendamento (para ficar arquivada no cliente).
                setForm((p) => ({ ...p, schedule_config: cfg }));
                if (client?.id) {
                  await save({ andSchedule: false, silent: true, extra: { schedule_config: cfg } });
                }
              }}
              onScheduled={() => onSaved(null)}
            />
          </TabsContent>
        </Tabs>

        {tab !== "schedule" && (
        <DialogFooter className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            {t("Cancelar")}
          </Button>
          {client?.id && (
            <Button
              onClick={() => save({ andSchedule: true })}
              data-testid="client-save-and-schedule-btn"
              variant="outline"
              className="w-full sm:w-auto border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={15} />
                {t("Salvar e agendar limpeza")}
              </span>
            </Button>
          )}
          <Button
            onClick={() => save()}
            data-testid="client-save-btn"
            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          >
            {client?.id ? t("Salvar alterações") : (
              <span className="inline-flex items-center gap-1.5">
                {t("Salvar e agendar limpeza")}
                <span aria-hidden>→</span>
              </span>
            )}
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// ClientFormProgress — usa FormProgress shared para mostrar campos essenciais
// ─────────────────────────────────────────────────────────────────────────
function ClientFormProgress({ form }) {
  const { t } = useI18n();
  const items = [
    { key: "name", label: t("Nome"), ok: !!(form.name || "").trim() },
    { key: "phone", label: t("Telefone"), ok: !!(form.phone || "").trim() },
    { key: "address", label: t("Endereço"), ok: !!(form.address || "").trim() },
    { key: "frequency", label: t("Frequência"), ok: !!form.frequency },
    {
      key: "value",
      label: t("Valor"),
      ok:
        Number(form.value) > 0 ||
        Number(form.rate_weekly) > 0 ||
        Number(form.rate_biweekly) > 0 ||
        Number(form.rate_monthly) > 0 ||
        Number(form.rate_other) > 0,
    },
  ];
  return <FormProgress items={items} testId="client-form-progress" />;
}



function Field({ label, req, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {typeof label === "string" ? tCase(label) : label} {req && <span className="text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

function ChecklistEditor({ value, onChange }) {
  const { t: tr } = useI18n();
  const [input, setInput] = React.useState("");
  function add() {
    const t = input.trim();
    if (!t) return;
    onChange([...(value || []), t]);
    setInput("");
  }
  function remove(i) {
    onChange((value || []).filter((_, idx) => idx !== i));
  }
  function move(i, dir) {
    const arr = [...(value || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  }
  const SUGGESTIONS = [
    "Aspirar carpetes",
    "Limpar banheiros",
    "Trocar roupas de cama",
    "Lavar louça",
    "Limpar geladeira",
    "Tirar lixo",
    "Limpar janelas",
    "Passar pano no chão",
  ];
  return (
    <div className="space-y-2" data-testid="client-checklist-editor">
      <div className="flex gap-2">
        <input
          data-testid="checklist-input"
          className="cm-input flex-1"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={tr("Ex: Aspirar carpetes da sala")}
        />
        <button
          type="button"
          onClick={add}
          data-testid="checklist-add-btn"
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
        >
          {tr("Adicionar")}
        </button>
      </div>
      {(value || []).length === 0 && (
        <div className="text-xs text-slate-500">
          {tr("Sugestões Rápidas:")}
          <div className="flex flex-wrap gap-1 mt-1">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChange([...(value || []), tr(s)])}
                className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 border border-slate-200"
              >
                + {tr(s)}
              </button>
            ))}
          </div>
        </div>
      )}
      {(value || []).length > 0 && (
        <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {value.map((it, i) => (
            <li
              key={`${i}-${it}`}
              className="flex items-center gap-2 px-3 py-2 text-sm"
              data-testid={`checklist-item-${i}`}
            >
              <span className="text-slate-400 text-xs w-6">{i + 1}.</span>
              <span className="flex-1">{it}</span>
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                title={tr("Subir")}
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === value.length - 1}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                title={tr("Descer")}
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`checklist-remove-${i}`}
                className="text-red-500 hover:text-red-700 text-sm"
                title={tr("Remover")}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
